import React, { useCallback } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';

import { SortingDirection, SortingOffersValue } from '../../constants/enums';

interface ISortableHeaderProps {
  label: string;
  sortValue: SortingOffersValue;
  currentSortValue: SortingOffersValue | null;
  currentDirection: SortingDirection;
  onSort: (
    sortValue: SortingOffersValue,
    direction: SortingDirection | null
  ) => void;
}

export const SortableHeader: React.FC<ISortableHeaderProps> = ({
  label,
  sortValue,
  currentSortValue,
  currentDirection,
  onSort
}): React.ReactElement => {
  const isActive = currentSortValue !== null && currentSortValue === sortValue;

  const handleClick = useCallback((): void => {
    if (!isActive) {
      onSort(sortValue, SortingDirection.ASC);
      return;
    }

    if (currentDirection === SortingDirection.ASC) {
      onSort(sortValue, SortingDirection.DESC);
      return;
    }

    // Third click on same column (DESC → clear)
    onSort(sortValue, null);
  }, [isActive, currentDirection, onSort, sortValue]);

  const isAsc = isActive && currentDirection === SortingDirection.ASC;
  const isDesc = isActive && currentDirection === SortingDirection.DESC;

  return (
    <div
      className="group cursor-pointer hover:text-base-foreground flex items-center gap-0.5"
      onClick={handleClick}
    >
      {label}
      {isActive ? (
        <div className="flex flex-col">
          {isAsc && (
            <ChevronUp
              className="h-3 w-3 text-foreground"
              strokeWidth={2}
            />
          )}
          {isDesc && (
            <ChevronDown
              className="h-3 w-3 text-foreground"
              strokeWidth={2}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col opacity-0 group-hover:opacity-50 transition-opacity">
          <ChevronUp
            className="h-3 w-3 -mb-[3px] text-muted-foreground"
            strokeWidth={2}
          />
          <ChevronDown
            className="h-3 w-3 -mt-[3px] text-muted-foreground"
            strokeWidth={2}
          />
        </div>
      )}
    </div>
  );
};

export default SortableHeader;
